import { Box, Button, DialogContent, Grid } from "@material-ui/core";
import { ToggleButton, ToggleButtonGroup } from "@material-ui/lab";
import { verifyError } from "Components/Common/helpers/verifyError";
import { useIsInternalAdmin } from "Components/Members/hooks/useIsInternalAdmin";
import ModalTitle from "Components/Modal/ModalTitle";
import {
	ForSchedulePhase,
	createSchedulePhase,
} from "Components/SchedulePhase/SchedulePhaseHelper";
import { useSchedulePhases } from "Components/SchedulePhase/hooks/useSchedulePhases";
import ColorField from "Fields/ColorField";
import DatePickerField from "Fields/DatePickerField";
import TextField from "Fields/TextField";
import { DEFAULT_COLOR_TAG } from "Helpers/ColorHelper";
import { useApp } from "Providers/AppProvider";
import { useTranslation } from "Providers/TranslationProvider";
import { ValidData } from "Store/types/ValidData";
import { Project } from "domain-entities";
import { useSnackbar } from "material-ui-snackbar-provider";
import moment, { Moment } from "moment";
import React, { FC, Fragment, useState } from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { getMembersAlphabetical } from "./ProjectHelper";
import ProjectSearchView from "./ProjectSearchView";
import { useProjects } from "./hooks/useProjects";

interface Props {
	project: Project;
	forSchedulePhase: ForSchedulePhase;
	close: (schedulePhaseId?: string) => void;
	selectExistingProject: (project: Project) => void;
}

interface FormValues {
	name: string;
	noteContent: string;
	colorTag: string;
	schedulePhaseStartDate: Moment | null;
	schedulePhaseEndDate: Moment | null;
}

const ProjectForm: FC<Props> = (props) => {
	const isInternalAdmin = useIsInternalAdmin();
	const { isNative } = useApp();
	const { t } = useTranslation();
	const [isExisting, setIsExisting] = useState(true);
	const snackbar = useSnackbar();
	const schedulePhases = useSchedulePhases((state) => state.items);

	const defaultValues: FormValues = {
		name: props.project.name,
		colorTag: props.project.colorTag || DEFAULT_COLOR_TAG,
		noteContent: props.project.noteContent || "",
		schedulePhaseStartDate: props.forSchedulePhase.start,
		schedulePhaseEndDate: props.forSchedulePhase.end,
	};

	const validationSchema = yup.object().shape({
		name: yup.string().required(
			t("Common_Form_Error_MandatoryField", {
				field: t("ProjectForm_Label_Name"),
			})
		),
		colorTag: yup.string().required(),
		schedulePhaseStartDate: yup
			.object<Moment>()
			.typeError(
				t("Common_Form_Error_MandatoryField", {
					field: t("ProjectForm_Label_SchedulePhaseStartDate"),
				})
			)
			.required()
			.test(
				"test-StartDate",
				t("Common_Form_Error_InvalidDate"),
				function (value) {
					return moment(value).isValid();
				}
			),
		schedulePhaseEndDate: yup
			.object<Moment>()
			.typeError(
				t("Common_Form_Error_MandatoryField", {
					field: t("ProjectForm_Label_SchedulePhaseEndDate"),
				})
			)
			.required()
			.test(
				"test-EndDate",
				t("Common_Form_Error_InvalidDate"),
				function (value) {
					return moment(value).isValid();
				}
			)
			.test(
				"test-endDateBeforeStartDate",
				t("Common_Form_Error_EndDateBeforeStartDate"),
				function (endDate: Moment) {
					if (!moment(this.parent.schedulePhaseStartDate).isValid()) {
						return true;
					}
					return endDate.isSameOrAfter(this.parent.schedulePhaseStartDate);
				}
			)
			.test(
				"test-alreadyAssigned",
				t("SchedulePhaseForm_Error_AlreadyAssigned"),
				function (endDate: Moment) {
					const startDate = this.parent.schedulePhaseStartDate as Moment;

					return !schedulePhases.find(
						(schedulePhase) =>
							schedulePhase.projectId === props.project.id &&
							startDate.isSameOrBefore(moment.unix(schedulePhase.endDate)) &&
							endDate.isSameOrAfter(moment.unix(schedulePhase.startDate))
					);
				}
			),
	});

	const form = useForm<FormValues>({ validationSchema, defaultValues });
	const { control } = form;
	const isDisabled = form.formState.isSubmitting || !isInternalAdmin;

	async function onSubmit(formValues: FormValues) {
		try {
			if (
				formValues.schedulePhaseStartDate &&
				formValues.schedulePhaseEndDate
			) {
				const projectItem: Partial<Project> & ValidData = {
					id: props.project.id,
					name: formValues.name,
					colorTag: formValues.colorTag,
					noteContent: formValues.noteContent || undefined,
					membersAlphabetical: getMembersAlphabetical(
						props.project.members,
						formValues.name
					),
				};

				await useProjects.getState().setProject({
					...props.project,
					...projectItem,
				});

				const schedulePhaseItem = createSchedulePhase(
					formValues.schedulePhaseStartDate,
					formValues.schedulePhaseEndDate,
					projectItem.id
				);

				await useSchedulePhases.getState().setItem(schedulePhaseItem);
				props.close(schedulePhaseItem.id);
			} else {
				throw new Error("Missing start or end date");
			}
		} catch (error) {
			snackbar.showMessage(verifyError(error).message);
		}
	}

	function renderFields() {
		if (isExisting) {
			return (
				<ProjectSearchView
					autoFocus={!isNative}
					onClickListItem={props.selectExistingProject}
				/>
			);
		} else {
			return (
				<Fragment>
					<Box mt={3}>
						<TextField
							name="name"
							label={t("ProjectForm_Label_Name")}
							control={control}
							disabled={isDisabled}
							autoFocus={!isNative}
						/>
					</Box>
					<Box mt={3}>
						<Grid container spacing={3}>
							<Grid item xs={12} sm={6}>
								<DatePickerField
									name="schedulePhaseStartDate"
									label={t("ProjectForm_Label_SchedulePhaseStartDate")}
									control={control}
									disabled={isDisabled}
								/>
							</Grid>
							<Grid item xs={12} sm={6}>
								<DatePickerField
									name="schedulePhaseEndDate"
									label={t("ProjectForm_Label_SchedulePhaseEndDate")}
									control={control}
									disabled={isDisabled}
								/>
							</Grid>
						</Grid>
					</Box>
					<Box mt={3}>
						<TextField
							name="noteContent"
							label={t("ProjectForm_Label_NoteContent")}
							control={control}
							disabled={isDisabled}
							multiline
						/>
					</Box>
					<Box mt={3}>
						<ColorField
							name="colorTag"
							label={t("ProjectForm_Label_ColorTag")}
							control={control}
							disabled={isDisabled}
						/>
					</Box>
				</Fragment>
			);
		}
	}

	return (
		<form onSubmit={form.handleSubmit((formValues) => onSubmit(formValues))}>
			<ModalTitle
				close={() => props.close()}
				title={
					isExisting
						? t("ProjectForm_Title_Select")
						: t("ProjectForm_Title_Create")
				}
			>
				{isInternalAdmin && !isExisting && (
					<Button
						type="submit"
						disabled={isDisabled}
						onClick={form.handleSubmit((formValues) => onSubmit(formValues))}
						variant="contained"
						color="primary"
					>
						{t("Common_Dialog_Create")}
					</Button>
				)}
			</ModalTitle>
			<DialogContent>
				<Box display="flex" justifyContent="center">
					<ToggleButtonGroup
						value={isExisting}
						onChange={(_, newValue: boolean | null) =>
							newValue !== null && setIsExisting(newValue)
						}
						exclusive
					>
						<ToggleButton value={true}>
							{t("ProjectForm_Toggle_IsExisting_True")}
						</ToggleButton>
						<ToggleButton value={false}>
							{t("ProjectForm_Toggle_IsExisting_False")}
						</ToggleButton>
					</ToggleButtonGroup>
				</Box>
				{renderFields()}
			</DialogContent>
		</form>
	);
};

export default ProjectForm;
